import type { FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  type Auth,
  type UserCredential,
} from 'firebase/auth';

/**
 * Référence interne vers l'instance Auth initialisée.
 *
 * On la mémorise pour que `signInWithEmail` / `signOutTaki` puissent fonctionner
 * sans avoir à repasser l'`app` à chaque appel. `initAuth(app)` doit donc être
 * appelé une fois au démarrage (après `loadTakiConfig` + `createTakiSdk`).
 */
let authInstance: Auth | null = null;

/** Instances Auth déjà branchées sur l'émulateur (branchement une seule fois). */
const emulatorBoundAuths = new WeakSet<Auth>();

/**
 * Adresse de l'émulateur Auth local. Volontairement structurel : `@taki/auth`
 * ne dépend pas de `@taki/runtime-config`, l'hôte lui passe simplement le bloc
 * `config.emulators?.auth`, qui a exactement cette forme.
 */
export interface AuthEmulatorEndpoint {
  host: string;
  port: number;
}

/**
 * Initialise (ou récupère) l'instance Firebase Auth pour l'app donnée.
 *
 * À appeler une fois au boot, en réutilisant le même `FirebaseApp` que celui
 * passé au SDK (`createTakiSdk(cfg).app`), afin de partager une seule instance Firebase.
 *
 * @param app - L'application Firebase déjà initialisée (via `@taki/sdk`).
 * @param emulator - Adresse de l'émulateur Auth local (dev 100% local, cf.
 *   `pnpm dev:local`) : passer `config.emulators?.auth`. Omis ou `undefined`
 *   (cas production) : Auth parle au cloud, comportement inchangé.
 * @returns L'instance `Auth` associée.
 */
export function initAuth(app: FirebaseApp, emulator?: AuthEmulatorEndpoint): Auth {
  authInstance = getAuth(app);

  if (emulator && !emulatorBoundAuths.has(authInstance)) {
    // `disableWarnings` : la bannière "emulator" du SDK n'apporte rien ici, le
    // branchement est explicite et voulu par la config.
    connectAuthEmulator(authInstance, `http://${emulator.host}:${emulator.port}`, {
      disableWarnings: true,
    });
    emulatorBoundAuths.add(authInstance);
    console.info(
      `[@taki/auth] Auth branché sur l'émulateur ${emulator.host}:${emulator.port} (comptes locaux jetables).`,
    );
  }

  return authInstance;
}

/**
 * Récupère l'instance Auth courante, ou lève une erreur explicite si `initAuth`
 * n'a pas encore été appelé.
 */
function requireAuth(): Auth {
  if (!authInstance) {
    throw new Error(
      '[@taki/auth] initAuth(app) doit être appelé avant toute opération d\'authentification.',
    );
  }
  return authInstance;
}

/**
 * Connexion par email / mot de passe (méthode de test du périmètre v0).
 *
 * @param email - Adresse email de l'utilisateur.
 * @param password - Mot de passe.
 * @returns Les identifiants Firebase résultants.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return signInWithEmailAndPassword(requireAuth(), email, password);
}

/**
 * Déconnexion de l'utilisateur courant.
 */
export async function signOutTaki(): Promise<void> {
  return signOut(requireAuth());
}

/**
 * Accès optionnel à l'instance Auth (utile au hook `useUser`).
 * Retourne `null` tant que `initAuth` n'a pas été appelé.
 */
export function getAuthInstance(): Auth | null {
  return authInstance;
}
